import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import DashboardLayout from "../../components/DashboardLayout";
import DealSubmissionModal from "../../components/DealSubmissionModal";
import AgentDealDetailModal from "../../components/AgentDealDetailModal";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Home, Clock, CheckCircle, Plus, FileText, MessageSquare } from "lucide-react";

const SellerDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDealModal, setShowDealModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [editingDeal, setEditingDeal] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [statsRes, dealsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true, headers }),
        axios.get(`${API}/deals`, { withCredentials: true, headers, params: { per_page: 5 } }),
      ]);
      setStats(statsRes.data);
      setDeals(dealsRes.data.deals || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const formatCurrency = (v) => {
    if (!v) return "$0";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
  };

  const getPropertyTypeLabel = (type) => {
    const labels = { single_family: "Single Family", multi_family: "Multi-Family", apartment: "Apartment", commercial: "Commercial", land: "Land", mixed_use: "Mixed Use" };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const styles = { pending: "bg-orange-500/10 text-orange-500", active: "bg-green-500/10 text-green-500", approved: "bg-blue-500/10 text-blue-500", closed: "bg-muted text-muted-foreground", changes_requested: "bg-yellow-500/10 text-yellow-700", rejected: "bg-red-500/10 text-red-500" };
    return styles[status] || "bg-muted text-muted-foreground";
  };

  return (
    <DashboardLayout title="Seller Dashboard">
      <div className="space-y-6 animate-fade-in" data-testid="seller-dashboard">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">Welcome back, {user?.name?.split(" ")[0]}!</h1>
            <p className="text-muted-foreground mt-1">List your properties and track their status</p>
          </div>
          <Button className="bg-green-500 hover:bg-green-600" onClick={() => setShowDealModal(true)} data-testid="submit-new-deal-btn">
            <Plus className="w-4 h-4 mr-2" /> Submit New Deal
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="stat-card" data-testid="stat-my-listings">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">My Listings</CardTitle>
              <div className="stat-card-icon bg-green-500/10"><Home className="w-5 h-5 text-green-500" /></div>
            </CardHeader>
            <CardContent>
              <div className="stat-card-value">{loading ? "-" : stats?.my_properties || 0}</div>
              <p className="stat-card-label">Total listed</p>
            </CardContent>
          </Card>
          <Card className="stat-card" data-testid="stat-pending">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pending Review</CardTitle>
              <div className="stat-card-icon bg-orange-500/10"><Clock className="w-5 h-5 text-orange-500" /></div>
            </CardHeader>
            <CardContent>
              <div className="stat-card-value">{loading ? "-" : stats?.pending || 0}</div>
              <p className="stat-card-label">Awaiting approval</p>
            </CardContent>
          </Card>
          <Card className="stat-card" data-testid="stat-approved">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Active</CardTitle>
              <div className="stat-card-icon bg-blue-500/10"><CheckCircle className="w-5 h-5 text-blue-500" /></div>
            </CardHeader>
            <CardContent>
              <div className="stat-card-value">{loading ? "-" : stats?.approved || 0}</div>
              <p className="stat-card-label">Live on market</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading">Your Listings</CardTitle>
            {deals.length > 0 && <span className="text-sm text-muted-foreground">{deals.length} shown</span>}
          </CardHeader>
          <CardContent>
            {deals.length === 0 ? (
              <div className="empty-state">
                <FileText className="empty-state-icon mx-auto" />
                <p className="empty-state-title">No listings yet</p>
                <p className="empty-state-desc">Start by listing your first property for sale</p>
                <Button className="mt-4 bg-green-500 hover:bg-green-600" onClick={() => setShowDealModal(true)} data-testid="empty-submit-deal-btn">
                  <Plus className="w-4 h-4 mr-2" /> Submit New Deal
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {deals.map((deal) => (
                  <div key={deal.deal_id} className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => setSelectedDeal(deal)} data-testid={`deal-item-${deal.deal_id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h4 className="font-medium text-foreground">{deal.property_address}</h4>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadge(deal.status)}`}>{deal.status}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{deal.city}, {deal.state} {deal.zip_code} · {getPropertyTypeLabel(deal.property_type)}</p>
                      {(deal.status === "changes_requested" || deal.status === "rejected") && deal.admin_notes && (
                        <div className={`flex items-center gap-1.5 mt-2 text-xs ${deal.status === "changes_requested" ? "text-yellow-700" : "text-red-600"}`}>
                          <MessageSquare className="w-3 h-3" />
                          <span className="line-clamp-1">{deal.admin_notes}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-foreground">{formatCurrency(deal.asking_price)}</p>
                      <p className="text-sm text-green-500">ARV: {formatCurrency(deal.estimated_arv)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <DealSubmissionModal
        open={showDealModal}
        onOpenChange={(open) => { setShowDealModal(open); if (!open) setEditingDeal(null); }}
        onSuccess={fetchData}
        draftData={editingDeal}
      />
      <AgentDealDetailModal
        open={!!selectedDeal}
        onOpenChange={(open) => { if (!open) setSelectedDeal(null); }}
        deal={selectedDeal}
        onEdit={(deal) => { setSelectedDeal(null); setEditingDeal(deal); setShowDealModal(true); }}
      />
    </DashboardLayout>
  );
};

export default SellerDashboard;
